import { useEffect, useRef, useState } from 'react'

/**
 * Whether JS-driven motion may run: the same gate as the `anim:` CSS variant (the
 * user turned animations on, and the OS isn't asking to reduce motion).
 */
export function motionAllowed(): boolean {
  if (typeof document === 'undefined' || !document.documentElement.classList.contains('animate-on')) return false
  return window.matchMedia?.('(prefers-reduced-motion: no-preference)').matches ?? false
}

/**
 * The OS-level signal alone, independent of this app's own (off-by-default)
 * animations setting — for browser-native motion (View Transitions) that would
 * otherwise run unconditionally and ignore the one preference that matters for it.
 */
export function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

/**
 * Notices when `value` changes after the first render. `key` counts the changes
 * (0 until the first), so keying a leaf element on it replays a CSS animation;
 * `prev` is the value before the latest change.
 */
export function useChange(value: number): { key: number; prev: number; up: boolean } {
  const [state, setState] = useState({ value, prev: value, key: 0 })
  if (state.value !== value) {
    const next = { value, prev: state.value, key: state.key + 1 }
    setState(next)
    return { key: next.key, prev: next.prev, up: value > next.prev }
  }
  return { key: state.key, prev: state.prev, up: state.value > state.prev }
}

/** `value`, counted up or down to each new value over `ms` when motion is allowed. */
export function useTweenedNumber(value: number, ms = 400): number {
  const [tween, setTween] = useState<number | null>(null)
  const from = useRef(value)
  useEffect(() => {
    const start = from.current
    from.current = value
    if (start === value || !motionAllowed()) return
    let raf = 0
    const began = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - began) / ms)
      if (t >= 1) return setTween(null)
      setTween(Math.round(start + (value - start) * (1 - (1 - t) ** 3)))
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      setTween(null)
    }
  }, [value, ms])
  return tween ?? value
}
